import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext,
} from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Paper, Button, TextField, InputAdornment, useMediaQuery,
  IconButton, Fab, Typography, Tooltip,
} from "@material-ui/core";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import ScheduleModal from "../../components/ScheduleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import moment from "moment";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "moment/locale/pt-br";
import "react-big-calendar/lib/css/react-big-calendar.css";
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import PersonIcon from "@material-ui/icons/Person";
import MessageIcon from "@material-ui/icons/Message";

function getUrlParam(paramName) {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(paramName);
}

const localizer = momentLocalizer(moment);

const defaultMessages = {
  date: i18n.t("schedules.date"),
  time: i18n.t("schedules.time"),
  event: i18n.t("schedules.event"),
  allDay: i18n.t("schedules.allDay"),
  week: i18n.t("schedules.week"),
  work_week: i18n.t("schedules.work_week"),
  day: i18n.t("schedules.day"),
  month: i18n.t("schedules.month"),
  previous: i18n.t("schedules.previous"),
  next: i18n.t("schedules.next"),
  yesterday: i18n.t("schedules.yesterday"),
  tomorrow: i18n.t("schedules.tomorrow"),
  today: i18n.t("schedules.today"),
  agenda: i18n.t("schedules.agenda"),
  noEventsInRange: i18n.t("schedules.noEventsInRange"),
  showMore: (total) => `+${total} más`,
};

const reducer = (state, action) => {
  if (action.type === "LOAD_SCHEDULES") {
    const schedules = action.payload;
    const newSchedules = [];
    schedules.forEach((schedule) => {
      const idx = state.findIndex((s) => s.id === schedule.id);
      if (idx !== -1) {
        state[idx] = schedule;
      } else {
        newSchedules.push(schedule);
      }
    });
    return [...state, ...newSchedules];
  }
  if (action.type === "UPDATE_SCHEDULES") {
    const schedule = action.payload;
    const idx = state.findIndex((s) => s.id === schedule.id);
    if (idx !== -1) {
      state[idx] = schedule;
      return [...state];
    }
    return [schedule, ...state];
  }
  if (action.type === "DELETE_SCHEDULE") {
    const scheduleId = action.payload;
    const idx = state.findIndex((s) => s.id === scheduleId);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("sm")]: {
      padding: 0,
    },
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(1),
    },
  },

  // ── CALENDARIO DESKTOP ───────────────────────────────────────────────
  calendarWrapper: {
    height: "calc(100vh - 180px)",
    minHeight: 480,
    "& .rbc-toolbar": {
      flexWrap: "wrap",
      gap: 4,
    },
    "& .rbc-toolbar-label": {
      color: theme.mode === "light" ? theme.palette.text.primary : "white",
      fontWeight: 600,
    },
    "& .rbc-btn-group button": {
      color: theme.mode === "light" ? theme.palette.text.primary : "white",
      borderRadius: 6,
      "&:hover": { background: "rgba(91,45,142,0.10)" },
      "&.rbc-active": {
        background: "rgba(91,45,142,0.15)",
        color: "#5B2D8E",
        borderColor: "rgba(91,45,142,0.30)",
      },
    },
    "& .rbc-event": {
      background: "linear-gradient(135deg, #5B2D8E, #7C3AED)",
      borderRadius: 6,
      border: "none",
      fontSize: 12,
    },
    "& .rbc-today": {
      background: theme.mode === "dark"
        ? "rgba(91,45,142,0.08)"
        : "rgba(91,45,142,0.05)",
    },
  },

  // ── CARDS MÓVIL ──────────────────────────────────────────────────────
  mobileHeader: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "8px 8px 0",
  },

  mobileSearchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      height: 40,
    },
    "& .MuiOutlinedInput-input": {
      padding: "0 14px",
      fontSize: 13,
    },
  },

  cardList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: theme.spacing(1),
  },

  card: {
    borderRadius: 14,
    border: "1px solid",
    borderColor: theme.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)",
    background: theme.mode === "dark"
      ? "linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(22,32,52,0.90) 100%)"
      : "#ffffff",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    overflow: "hidden",
    transition: "all 0.18s ease",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(91,45,142,0.12)",
      borderColor: "rgba(91,45,142,0.25)",
    },
  },

  cardAccent: {
    height: 4,
    background: "linear-gradient(90deg, #5B2D8E, #7C3AED)",
  },

  cardBody: {
    padding: "10px 14px 12px",
  },

  cardTopRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 8,
  },

  cardContact: {
    fontWeight: 700,
    fontSize: 15,
    color: theme.mode === "dark" ? "#e2e8f0" : "#1e293b",
    display: "flex",
    alignItems: "center",
    gap: 5,
  },

  cardActions: {
    display: "flex",
    gap: 4,
    flexShrink: 0,
  },

  cardActionBtn: {
    width: 32,
    height: 32,
    padding: 0,
    borderRadius: 8,
    border: "1px solid",
    borderColor: theme.mode === "dark" ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)",
    background: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.15s ease",
  },

  cardMeta: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },

  cardMetaRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 6,
    fontSize: 12,
    color: theme.mode === "dark" ? "#64748b" : "#94a3b8",
  },

  cardMetaIcon: {
    fontSize: "14px !important",
    marginTop: 1,
    flexShrink: 0,
    color: "#5B2D8E",
  },

  cardMetaText: {
    fontSize: 12,
    color: theme.mode === "dark" ? "#94a3b8" : "#475569",
    overflow: "hidden",
    display: "-webkit-box",
    "-webkit-line-clamp": 2,
    "-webkit-box-orient": "vertical",
  },

  statusChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: 6,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.03em",
    marginTop: 6,
  },

  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    gap: 12,
    color: theme.mode === "dark" ? "#475569" : "#94a3b8",
  },

  fab: {
    position: "fixed",
    bottom: 24,
    right: 24,
    background: "linear-gradient(135deg, #5B2D8E, #7C3AED)",
    color: "#fff",
    zIndex: 1200,
    boxShadow: "0 4px 16px rgba(91,45,142,0.40)",
    "&:hover": {
      background: "linear-gradient(135deg, #0280d9, #1eb8b0)",
    },
  },
}));

const statusConfig = {
  PENDING:   { label: "Pendiente",  bg: "#fef3c7", color: "#92400e" },
  SCHEDULED: { label: "Programado", bg: "rgba(91,45,142,0.12)", color: "#5B2D8E" },
  SENT:      { label: "Enviado",    bg: "#dcfce7", color: "#166534" },
  ERROR:     { label: "Error",      bg: "#fee2e2", color: "#991b1b" },
};

const Schedules = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [deletingSchedule, setDeletingSchedule] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [schedules, dispatch] = useReducer(reducer, []);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [contactId, setContactId] = useState(+getUrlParam("contactId"));

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useSchedules) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => history.push("/"), 1000);
      }
    }
    fetchData();
  }, [user, history, getPlanCompany]);

  const fetchSchedules = useCallback(async () => {
    try {
      const { data } = await api.get("/schedules", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_SCHEDULES", payload: data.schedules });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchSchedules();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, contactId, fetchSchedules]);

  useEffect(() => {
    const onCompanySchedule = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_SCHEDULES", payload: data.schedule });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_SCHEDULE", payload: +data.scheduleId });
      }
    };
    socket.on(`company${user.companyId}-schedule`, onCompanySchedule);
    return () => socket.off(`company${user.companyId}-schedule`, onCompanySchedule);
  }, [socket]);

  const cleanContact = () => setContactId("");

  const handleOpenScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(false);
  };

  const handleSearch = (event) => setSearchParam(event.target.value.toLowerCase());

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setScheduleModalOpen(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success(i18n.t("schedules.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingSchedule(null);
    setSearchParam("");
    dispatch({ type: "RESET" });
    setPageNumber(1);
    await fetchSchedules();
  };

  const loadMore = () => setPageNumber((prev) => prev + 1);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) loadMore();
  };

  const truncate = (str, len) => {
    if (!str) return "";
    return str.length > len ? str.substring(0, len) + "..." : str;
  };

  // ── RENDER CARD MÓVIL ─────────────────────────────────────────────
  const renderMobileCard = (schedule) => {
    const sendAt = moment(schedule.sendAt);
    const isToday = sendAt.isSame(moment(), "day");
    const isPast = sendAt.isBefore(moment());
    const status = statusConfig[schedule.status] || statusConfig.PENDING;

    return (
      <div key={schedule.id} className={classes.card}>
        <div className={classes.cardAccent} />
        <div className={classes.cardBody}>
          <div className={classes.cardTopRow}>
            <div className={classes.cardContact}>
              <PersonIcon style={{ fontSize: 16, color: "#5B2D8E" }} />
              {schedule.contact?.name || "Sin contacto"}
            </div>
            <div className={classes.cardActions}>
              <Tooltip title="Editar">
                <IconButton
                  size="small"
                  className={classes.cardActionBtn}
                  onClick={() => handleEditSchedule(schedule)}
                >
                  <EditIcon style={{ fontSize: 16, color: "#5B2D8E" }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Eliminar">
                <IconButton
                  size="small"
                  className={classes.cardActionBtn}
                  onClick={() => {
                    setDeletingSchedule(schedule);
                    setConfirmModalOpen(true);
                  }}
                >
                  <DeleteOutlineIcon style={{ fontSize: 16, color: "#ef4444" }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          <div className={classes.cardMeta}>
            <div className={classes.cardMetaRow}>
              <AccessTimeIcon className={classes.cardMetaIcon} />
              <span className={classes.cardMetaText} style={{
                fontWeight: isToday ? 700 : 400,
                color: isToday ? "#5B2D8E" : isPast ? "#ef4444" : undefined,
              }}>
                {isToday
                  ? `Hoy ${sendAt.format("HH:mm")}`
                  : sendAt.format("DD/MM/YYYY HH:mm")}
              </span>
            </div>

            {schedule.body && (
              <div className={classes.cardMetaRow}>
                <MessageIcon className={classes.cardMetaIcon} />
                <span className={classes.cardMetaText}>
                  {truncate(schedule.body, 80)}
                </span>
              </div>
            )}
          </div>

          <span
            className={classes.statusChip}
            style={{ background: status.bg, color: status.color }}
          >
            {status.label}
          </span>
        </div>
      </div>
    );
  };

  // Eventos para el calendario desktop
  const calendarEvents = schedules.map((schedule) => ({
    title: (
      <div
        style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}
        onClick={(e) => e.stopPropagation()}
      >
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {schedule?.contact?.name}
        </span>
        <DeleteOutlineIcon
          style={{ fontSize: 14, cursor: "pointer", opacity: 0.7 }}
          onClick={(e) => { e.stopPropagation(); handleDeleteSchedule(schedule.id); }}
        />
        <EditIcon
          style={{ fontSize: 14, cursor: "pointer", opacity: 0.7 }}
          onClick={(e) => { e.stopPropagation(); handleEditSchedule(schedule); }}
        />
      </div>
    ),
    start: new Date(schedule.sendAt),
    end: new Date(schedule.sendAt),
  }));

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingSchedule && i18n.t("schedules.confirmationModal.deleteTitle")}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteSchedule(deletingSchedule.id)}
      >
        {i18n.t("schedules.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      {scheduleModalOpen && (
        <ScheduleModal
          open={scheduleModalOpen}
          onClose={handleCloseScheduleModal}
          reload={fetchSchedules}
          scheduleId={selectedSchedule ? selectedSchedule.id : null}
          contactId={contactId}
          cleanContact={cleanContact}
          user={user}
        />
      )}

      {/* ── HEADER ──────────────────────────────────────────────────── */}
      {isMobile ? (
        <div className={classes.mobileHeader}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6" style={{ fontWeight: 700, fontSize: 16 }}>
              {i18n.t("schedules.title")}
              <span style={{ fontSize: 13, fontWeight: 400, color: "#94a3b8", marginLeft: 6 }}>
                ({schedules.length})
              </span>
            </Typography>
          </div>
          <TextField
            variant="outlined"
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            fullWidth
            className={classes.mobileSearchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
          />
        </div>
      ) : (
        <MainHeader>
          <Title>
            {i18n.t("schedules.title")} ({schedules.length})
          </Title>
          <MainHeaderButtonsWrapper>
            <TextField
              placeholder={i18n.t("contacts.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="contained" color="primary" onClick={handleOpenScheduleModal}>
              {i18n.t("schedules.buttons.add")}
            </Button>
          </MainHeaderButtonsWrapper>
        </MainHeader>
      )}

      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        {/* ── VISTA MÓVIL: CARDS ───────────────────────────────────── */}
        {isMobile ? (
          <div className={classes.cardList}>
            {schedules.length === 0 && !loading ? (
              <div className={classes.emptyState}>
                <AccessTimeIcon style={{ fontSize: 48, opacity: 0.3 }} />
                <Typography variant="body2" style={{ textAlign: "center" }}>
                  No hay programaciones.<br />Toca + para crear una.
                </Typography>
              </div>
            ) : (
              schedules
                .slice()
                .sort((a, b) => new Date(a.sendAt) - new Date(b.sendAt))
                .map((schedule) => renderMobileCard(schedule))
            )}
            {loading && (
              <div style={{ textAlign: "center", padding: 16, color: "#94a3b8", fontSize: 13 }}>
                Cargando...
              </div>
            )}
          </div>
        ) : (
          // ── VISTA DESKTOP: CALENDARIO ─────────────────────────────
          <div className={classes.calendarWrapper}>
            <Calendar
              messages={defaultMessages}
              formats={{
                agendaDateFormat: "DD/MM ddd",
                weekdayFormat: "dddd",
              }}
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              style={{ height: "100%" }}
            />
          </div>
        )}
      </Paper>

      {/* FAB para nueva programación en móvil */}
      {isMobile && (
        <Fab className={classes.fab} onClick={handleOpenScheduleModal}>
          <AddIcon />
        </Fab>
      )}
    </MainContainer>
  );
};

export default Schedules;
